import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { GENERAL_INFO_FORM_SCHEMA } from '../validationSchema';
import { toGeneralInfoFieldPatch } from '../utils';
import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch } from '../../../../../../../store';
import type { GeneralInfoFieldName, GeneralInfoFieldValue } from '../types';
import type { GeneralFormValues } from '../../../../../common';
import type { FormikProps } from 'formik';

export default function useSaveIntakeGeneralInfoFieldOnBlur(
  formik: FormikProps<GeneralFormValues>,
  initialValues: GeneralFormValues,
) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const savedValuesRef = useRef<GeneralFormValues>(initialValues);
  const currentValues = formik.values;

  useEffect(() => {
    savedValuesRef.current = initialValues;
  }, [initialValues]);

  return useCallback(
    async (name: GeneralInfoFieldName, value?: GeneralInfoFieldValue) => {
      if (!intakeQuestionnaireId) {
        return;
      }

      const values = value === undefined ? currentValues : { ...currentValues, [name]: value };

      if (
        name === 'client_date_of_birth' &&
        values.client_date_of_birth != null &&
        !values.client_date_of_birth.isValid()
      ) {
        return;
      }

      try {
        await GENERAL_INFO_FORM_SCHEMA.validateAt(name, values);
      } catch {
        return;
      }

      const patch = toGeneralInfoFieldPatch(name, values);
      const savedPatch = toGeneralInfoFieldPatch(name, savedValuesRef.current);
      if (patch[name] === savedPatch[name]) {
        return;
      }

      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeGeneralInfo({
          intakeQuestionnaireId,
          data: patch,
          getAccessTokenSilently,
        }),
      );

      if (intakeQuestionnaireActions.updateIntakeGeneralInfo.fulfilled.match(result)) {
        savedValuesRef.current = { ...savedValuesRef.current, [name]: values[name] };
      }
    },
    [dispatch, currentValues, getAccessTokenSilently, intakeQuestionnaireId],
  );
}
